import { Component, type ErrorInfo, type ReactNode } from 'react'

type RouteErrorBoundaryProps = {
  children: ReactNode
  /** Short label for the screen that failed (e.g. "Patient details"). */
  title?: string
}

type RouteErrorBoundaryState = {
  error: Error | null
}

export default class RouteErrorBoundary extends Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  state: RouteErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[RouteErrorBoundary]', error, info.componentStack)
  }

  private handleRetry = () => {
    this.setState({ error: null })
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children

    const title = this.props.title ?? 'This page'
    return (
      <div className="flex min-h-[50vh] items-center justify-center p-6">
        <div className="max-w-lg rounded-xl border border-rose-200 bg-rose-50 p-6 text-center shadow-sm">
          <h2 className="text-base font-semibold text-rose-900">
            {title} could not be displayed
          </h2>
          <p className="mt-2 text-sm text-rose-800">
            {error.message || 'An unexpected error occurred while rendering this screen.'}
          </p>
          <button
            type="button"
            onClick={this.handleRetry}
            className="mt-4 inline-flex items-center rounded-lg bg-rose-700 px-4 py-2 text-sm font-medium text-white hover:bg-rose-800"
          >
            Try again
          </button>
        </div>
      </div>
    )
  }
}
